import type { TvStanding } from "./tv-mode";

/** How long each page of the TV standings stays up before the next one. */
export const TV_STANDINGS_PAGE_MS = 9000;
/** Fewer rows than this per page and the list stops reading like standings. */
export const TV_STANDINGS_MIN_ROWS = 3;
/** More rows than this and names get too small to read from the couch. */
export const TV_STANDINGS_MAX_ROWS = 10;

export interface TvStandingRow extends TvStanding {
  /** Someone else holds the same place. */
  tied: boolean;
}

export interface TvPodiumPlace {
  rank: 1 | 2 | 3;
  /** The first person at this place. Anyone tied with them is in the list. */
  lead: TvStandingRow;
  /** Everyone at this place, the lead included. */
  tiedCount: number;
}

export interface TvStandingsPage {
  rows: TvStandingRow[];
  firstRank: number;
  lastRank: number;
}

export interface TvStandingsPlan {
  podium: TvPodiumPlace[];
  pages: TvStandingsPage[];
  total: number;
}

function isPodiumPlace(rank: number): rank is 1 | 2 | 3 {
  return rank === 1 || rank === 2 || rank === 3;
}

/**
 * Splits the standings into a podium (one name per place) and evenly sized
 * pages for everyone else, so a TV can rotate through any party size with
 * every row at a readable size. Pages differ by at most one row.
 */
export function planTvStandings(
  standings: readonly TvStanding[],
  rowsPerPage: number,
): TvStandingsPlan {
  const ordered = standings
    .map((standing, index) => ({ standing, index }))
    .sort((left, right) => left.standing.rank - right.standing.rank || left.index - right.index)
    .map(({ standing }) => standing);
  const counts = new Map<number, number>();
  for (const standing of ordered) {
    counts.set(standing.rank, (counts.get(standing.rank) ?? 0) + 1);
  }
  const rows: TvStandingRow[] = ordered.map((standing) => ({
    ...standing,
    tied: (counts.get(standing.rank) ?? 0) > 1,
  }));

  const podium: TvPodiumPlace[] = [];
  const rest: TvStandingRow[] = [];
  for (const row of rows) {
    if (isPodiumPlace(row.rank) && !podium.some((place) => place.rank === row.rank)) {
      podium.push({ rank: row.rank, lead: row, tiedCount: counts.get(row.rank) ?? 1 });
    } else {
      rest.push(row);
    }
  }

  const size = Math.max(1, Math.floor(Number.isFinite(rowsPerPage) ? rowsPerPage : 1));
  const pageCount = Math.ceil(rest.length / size);
  const pages: TvStandingsPage[] = [];
  let start = 0;
  for (let page = 0; page < pageCount; page++) {
    const take = Math.ceil((rest.length - start) / (pageCount - page));
    const slice = rest.slice(start, start + take);
    start += take;
    pages.push({
      rows: slice,
      firstRank: slice[0]?.rank ?? 0,
      lastRank: slice.at(-1)?.rank ?? 0,
    });
  }

  return { podium, pages, total: rows.length };
}

/** Page to show after `tick` rotations. Wraps, and survives a shrinking page count. */
export function tvStandingsPageIndex(pageCount: number, tick: number): number {
  if (!Number.isFinite(pageCount) || pageCount <= 1 || !Number.isFinite(tick) || tick < 0) {
    return 0;
  }
  return Math.floor(tick) % Math.floor(pageCount);
}

/** Rows that fit a standings column of `heightPx` at a readable row height. */
export function tvStandingsRowsPerPage(heightPx: number, rowPx: number): number {
  if (!Number.isFinite(heightPx) || !Number.isFinite(rowPx) || heightPx <= 0 || rowPx <= 0) {
    return TV_STANDINGS_MIN_ROWS;
  }
  return Math.min(
    TV_STANDINGS_MAX_ROWS,
    Math.max(TV_STANDINGS_MIN_ROWS, Math.floor(heightPx / rowPx)),
  );
}

/** Up to two letters for an avatar without a photo: first and last word. */
export function tvInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = Array.from(words[0] ?? "")[0] ?? "";
  const last = words.length > 1 ? (Array.from(words.at(-1) ?? "")[0] ?? "") : "";
  return `${first}${last}`.toLocaleUpperCase();
}
